import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { MessageSquare, ArrowLeft, Send, Search, Phone, Paperclip } from 'lucide-react';
import { deptDisplay } from '../lib/departments';
import useAutoRefresh from '../hooks/useAutoRefresh';
import {
  AttachMenu, PendingAttachments, AttachmentList,
  filterNewFiles, uploadAttachments, useFileDrop,
} from './MessageAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  orange:  { icon: 'text-orange-400',  badge: 'bg-orange-500',  mine: 'ml-auto bg-orange-500/15 border border-orange-500/30',  link: 'text-orange-400' },
  emerald: { icon: 'text-emerald-400', badge: 'bg-emerald-500', mine: 'ml-auto bg-emerald-500/15 border border-emerald-500/30', link: 'text-emerald-400' },
  purple:  { icon: 'text-purple-400',  badge: 'bg-purple-500',  mine: 'ml-auto bg-purple-500/15 border border-purple-500/30',  link: 'text-purple-400' },
  blue:    { icon: 'text-blue-400',    badge: 'bg-blue-500',    mine: 'ml-auto bg-blue-500/15 border border-blue-500/30',    link: 'text-blue-400' },
};

// Shared department view over the EXISTING staff-messages system. The server
// locks dept members to their own channel; employees see the DEPARTMENT name.
export const DeptMessages = ({ dept, color = 'orange', testPrefix = 'dept' }) => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const addFiles = (incoming) => setFiles((prev) => [...prev, ...filterNewFiles(prev, incoming)]);
  const { dragOver, dropProps } = useFileDrop(addFiles);

  const fetchOverview = () => {
    axios.get(`${API}/admin/staff-messages/overview`)
      .then((r) => setOverview(r.data?.staff || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(fetchOverview, []);

  const openThread = (emp) => {
    setActive(emp);
    axios.get(`${API}/admin/employees/${emp.id}/messages`).then((r) => setThread(r.data || [])).catch(() => setThread([]));
  };

  const activeRef = useRef(null);
  activeRef.current = active;
  useAutoRefresh(() => {
    const a = activeRef.current;
    if (a) {
      axios.get(`${API}/admin/employees/${a.id}/messages`).then((r) => setThread(r.data || [])).catch(() => {});
    } else {
      fetchOverview();
    }
  }, { match: ['/messages'], intervalMs: 20000 });

  const send = async () => {
    if (!body.trim() && !files.length) return;
    setSending(true);
    try {
      const payload = { body };
      if (files.length) payload.attachment_ids = await uploadAttachments(files);
      const r = await axios.post(`${API}/admin/employees/${active.id}/messages`, payload);
      setThread((t) => [...t, r.data]);
      setBody('');
      setFiles([]);
      toast.success('Message sent — the employee is notified by email');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const filtered = overview.filter((o) => !search || (o.name || '').toLowerCase().includes(search.toLowerCase()));
  const label = deptDisplay(dept);
  const c = COLORS[color] || COLORS.orange;

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="border-b border-border pb-4 flex items-center gap-3">
        <MessageSquare className={`w-6 h-6 ${c.icon}`} strokeWidth={1.5} />
        <div>
          <h1 className="text-2xl font-barlow tracking-widest">STAFF MESSAGES</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Employees see your messages as <b>{label}</b> — never your personal name.
          </p>
        </div>
      </div>

      {!active ? (
        <>
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff by name..." className="pl-9 rounded-sm" data-testid={`${testPrefix}-messages-search`} />
          </div>
          {loading ? <p className="py-10 text-center text-muted-foreground animate-pulse">Loading…</p> : (
            <div className="divide-y divide-border border border-border rounded-sm bg-card" data-testid={`${testPrefix}-messages-list`}>
              {filtered.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No staff found.</p>}
              {filtered.map((o) => (
                <button key={o.id} type="button" onClick={() => openThread(o)} className="w-full text-left p-3 hover:bg-accent/40 flex items-center justify-between gap-2" data-testid={`${testPrefix}-msg-row-${o.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{o.name}</p>
                    {o.last_body && <p className="text-xs text-muted-foreground truncate">{o.last_body}</p>}
                  </div>
                  {(o.unread || 0) > 0 && <Badge className={`${c.badge} text-black rounded-full text-xs shrink-0`}>{o.unread}</Badge>}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setActive(null); fetchOverview(); }} data-testid={`${testPrefix}-msg-back`}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <p className="font-medium">{active.name}</p>
            {active.phone && (
              <a href={`tel:${active.phone}`} className={`ml-auto inline-flex items-center gap-1 text-xs underline ${c.link}`} data-testid={`${testPrefix}-msg-phone`}>
                <Phone className="w-3.5 h-3.5" /> {active.phone}
              </a>
            )}
          </div>
          <div className="border border-border rounded-sm bg-card p-3 space-y-2 max-h-[50vh] overflow-y-auto" data-testid={`${testPrefix}-msg-thread`}>
            {thread.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No messages yet.</p>}
            {thread.map((m) => (
              <div key={m.id} className={`max-w-[85%] rounded-sm px-3 py-2 text-sm ${m.sender_role === 'admin' ? c.mine : 'bg-muted/60 border border-border'}`}>
                <p className="text-[10px] text-muted-foreground mb-0.5">{m.sender_name} · {new Date(m.created_at).toLocaleString('en-GB')}</p>
                {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                <AttachmentList attachments={m.attachments} />
              </div>
            ))}
          </div>
          <div className="relative" {...dropProps}>
            {dragOver && (
              <div className="absolute inset-0 z-10 bg-primary/10 border-2 border-dashed border-primary rounded-sm flex items-center justify-center pointer-events-none" data-testid={`${testPrefix}-attach-drop-overlay`}>
                <p className="text-xs uppercase tracking-wider text-primary font-semibold flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Drop files to attach
                </p>
              </div>
            )}
            <PendingAttachments files={files} onRemove={(i) => setFiles((p) => p.filter((_, n) => n !== i))} testPrefix={testPrefix} />
            <div className="flex gap-2 pt-2">
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Type a message…" className="rounded-sm flex-1" data-testid={`${testPrefix}-msg-input`} />
              <AttachMenu onFiles={addFiles} disabled={sending} testPrefix={testPrefix} />
              <Button disabled={sending || (!body.trim() && !files.length)} onClick={send} className="rounded-sm self-end" data-testid={`${testPrefix}-msg-send`}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeptMessages;
